import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AnimatingDots from '~/components/AnimatingDots';
import BillingBanner from '~/components/BillingBanner';
import Button from '~/components/Button';
import Gap from '~/components/Gap';
import Header from '~/components/Header';
import MainContainer from '~/components/MainContainer';
import PaginationBar from '~/components/PaginationBar';
import { MobileCtaContext } from '~/components/SidebarLayout';
import { ADD_INVOICE_ROUTE, BILLING_ROUTE } from '~/constants/routes';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import { analytics, ANALYTICS_EVENTS } from '~/services/analyticsService';
import ClientTable from './Clientable';
import ClientList from './ClientList';
import DeleteClient from './DeleteClient';
import EmptyClientList from './EmptyClientList';
import { useClientList } from './hooks/useClientList';
import { ClientListContext } from './hooks/useClientListContext';
import AddClientModal from './AddClient/AddClientModal';

const Client = () => {
  const {
    isLoading,
    listData,
    listIds,
    clientId,
    setClientId,
    totalPages,
    totalCount,
    currentPage,
    isModalOpen,
    modalActions,
    fetchSpecificPage,
    deleteClientId,
    setDeleteClientId,
    canCreateClient,
  } = useClientList();

  const navigate = useNavigate();

  const handleAddClient = () => {
    analytics.capture(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      screen_name: 'Clients',
      button_name: 'New Client',
      button_id: 'new_client',
      section: 'Header',
    });

    if (canCreateClient) {
      modalActions.toggle();
    } else {
      navigate(`${BILLING_ROUTE}?ref=clients`);
    }
  };

  const handleBillingBannerClick = () => {
    analytics.capture(ANALYTICS_EVENTS.LINK_CLICKED, {
      screen_name: 'Clients',
      link_name: 'Upgrade now',
      link_id: 'upgrade_banner',
      section: 'Banner',
    });
    navigate(`${BILLING_ROUTE}?ref=clients`);
  };

  function onCloseClientModal() {
    setClientId(null);
    modalActions.off();
  }

  function onOpenEditClientModal(clientUuid: string) {
    setClientId(clientUuid);
    modalActions.on();
  }

  function onCreateInvoice(clientUuid: string) {
    navigate(`${ADD_INVOICE_ROUTE}?clientId=${clientUuid}`);
  }

  function onOpenDeleteClientModal(clientUuid: string) {
    setDeleteClientId(clientUuid);
  }

  const isDesktop = useIsDesktop();

  const { setMobileCta } = useContext(MobileCtaContext);

  useEffect(() => {
    setMobileCta(
      <Button variant="primary" className="full-width" size="sm" onClick={handleAddClient}>
        + New Client
      </Button>,
    );
    return () => setMobileCta(null);
  }, [canCreateClient, navigate, modalActions.toggle, setMobileCta]);

  return (
    <div>
      <Header
        headerText="Clients"
        rightJsx={
          isDesktop && (
            <Button variant="primary" size={isDesktop ? 'smd' : 'sm'} onClick={handleAddClient}>
              + New Client
            </Button>
          )
        }
      />

      <AddClientModal
        isModalOpen={isModalOpen}
        onCloseClientModal={onCloseClientModal}
        heading={clientId ? 'Edit Client' : 'Add New Client'}
        clientId={clientId}
      />

      <DeleteClient
        clientUuid={deleteClientId}
        handleClose={() => {
          setDeleteClientId(null);
        }}
      />

      <MainContainer>
        {!canCreateClient && (
          <BillingBanner
            variant="error"
            title="Client Limit Reached. Time to Scale. 🚀"
            description="You've successfully added clients on the Free Plan, great job!. Upgrade now to manage unlimited clients and stay organized."
            buttonText="Upgrade now"
            onButtonClick={handleBillingBannerClick}
          />
        )}

        <ClientListContext.Provider
          value={{
            listIds,
            listData,
            onOpenEditClientModal,
            onOpenDeleteClientModal,
            onCreateInvoice,
          }}
        >
          {!isLoading && listIds.length === 0 && (
            <EmptyClientList
              handleAddClient={() => {
                modalActions.toggle();
              }}
            />
          )}
          {listIds.length > 0 && (
            <div className="full-width">
              {isDesktop ? <ClientTable /> : <ClientList />}
              <PaginationBar
                totalItems={totalCount}
                totalPages={totalPages}
                itemsPerPage={10}
                currentPage={currentPage}
                onPageChange={fetchSpecificPage}
                onItemsPerPageChange={() => {}}
              />
            </div>
          )}
        </ClientListContext.Provider>
      </MainContainer>

      {isLoading && (
        <div className="full-width center">
          <AnimatingDots />
        </div>
      )}

      <Gap t={32} />
    </div>
  );
};

export default Client;
